import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { differenceInYears, format, parseISO } from "date-fns";
import { Chip, Tooltip } from "@mui/joy";
import { Avatar, Button, Box, CircularProgress, Typography, Card } from "@mui/joy";

import { supabase } from "../supabaseClient";
import { fetchProfilePicture, getInitials, formatStudentName, getStudentBirthdayStatus } from "../utils";

import { useModal } from "../context/ModalContext";

import { Student as DBStudent, GradeLevelLabels } from "../types";

import Page, { Breadcrumb } from "../components/Page";
import AddEditStudentModal from "../components/modals/AddEditStudentModal";

import ParentGuardiansTable from "../components/tables/StudentParentGuardiansTable";
import ClassesTable from "../components/tables/ClassesTable";
import StudentAttendanceTable from "../components/tables/StudentAttendanceTable";

type StudentWithProfilePicture = DBStudent & { profile_picture?: string | null };

const STUDENT_PROFILE_PICTURES: string = "students";

const StudentDetailView: React.FC = () => {
  const { studentId } = useParams();
  const { openModal } = useModal();
  const [student, setStudent] = React.useState<StudentWithProfilePicture | null>(null);
  const [breadcrumbs, setBreadcrumbs] = React.useState<Breadcrumb[]>([
    { label: "Home", href: "/" },
    { label: "Students", href: "/students" },
    { label: "Viewing Student" },
  ]);

  // Fetch a single student with relationships and profile pictures for guardians
  const fetchStudent = async (studentId: string | null | undefined) => {
    if (!studentId) {
      return null;
    }

    try {
      const { data, error } = await supabase
        .from("students")
        .select(`*`)
        .eq("id", studentId)
        .single();

      if (error || !data) {
        throw error;
      }

      const profilePicture = await fetchProfilePicture(STUDENT_PROFILE_PICTURES, data.profile_picture_id);

      return {
        ...data,
        profile_picture: profilePicture,
      } as StudentWithProfilePicture;
    } catch (error) {
      console.error("Error fetching student:", error);
      return null;
    }
  };

  useEffect(() => {
    fetchStudent(studentId).then((student) => {
      setStudent(student);
    });
  }, [studentId]);

  useEffect(() => {
    if (student) {
      setBreadcrumbs([
        { label: "Home", href: "/" },
        { label: "Students", href: "/students" },
        { label: formatStudentName(student) },
      ]);
    }
  }, [student]);

  // Subscribe to real-time changes for this specific student
  useEffect(() => {
    if (!studentId) return;

    // Subscribe to changes in the "students" table for the specific student
    const subscription = supabase
      .channel("public:students")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "students", filter: `id=eq.${studentId}` },
        (payload) => {
          // console.log("Student change received!", payload);
          fetchStudent(studentId).then((updatedStudent) => {
            setStudent(updatedStudent);
          });
        }
      )
      .subscribe();

    // Cleanup the subscription on component unmount
    return () => {
      supabase.removeChannel(subscription);
    };
  }, [studentId]);

  if (!student) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const formattedDOB = student.date_of_birth
    ? format(parseISO(student.date_of_birth), "MM/dd/yyyy")
    : "N/A";

  const calculateAge = (dateOfBirth: string | null) => {
    if (!dateOfBirth) return null;
    return differenceInYears(new Date(), new Date(dateOfBirth));
  };

  const handleEditStudent = () => {
    // Open the modal to edit the student
    openModal(<AddEditStudentModal student={student} />);
  };

  return (
    <Page breadcrumbs={breadcrumbs}>
      <Card
        variant="soft"
        sx={{
          display: "flex",
          alignItems: "center",
          position: "relative",
          p: 3,
          mb: 4,
          width: "100%",
          backgroundColor: "background.level2",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, flex: 1, width: "100%" }}>
          <Avatar
            key={`student-avatar-${student.id}`}
            src={student.profile_picture || undefined}
            sx={{ width: 128, height: 128 }}
          >
            {!student.profile_picture ? (
              <Typography sx={{ fontSize: 48 }}>
                {getInitials(student)}
              </Typography>
            ) : null}
          </Avatar>

          <Box>
            <Typography level="h2" component="h1" sx={{ mb: 1 }}>
              {formatStudentName(student)}
              {(() => {
                const birthdayStatus = getStudentBirthdayStatus(student.date_of_birth);

                if (birthdayStatus) {
                  return (
                    <Tooltip title={`Birthdate: ${format(parseISO(student.date_of_birth || ""), "MMMM do, yyyy")}`}>
                      <Chip
                        variant="soft"
                        color="success"
                        sx={{
                          ml: 2,
                          display: "inline-flex",
                          alignItems: "center",
                          lineHeight: 1,
                          verticalAlign: "baseline",
                        }}
                      >
                        🎂 {birthdayStatus === 'today' ? 'Birthday Today!' : 'Birthday Week!'}
                      </Chip>
                    </Tooltip>
                  );
                }
                return null;
              })()}
            </Typography>
            <Typography>
              <Typography component="span" fontWeight="bold">
                Date of Birth:
              </Typography>{" "}
              {formattedDOB}{" "}
              <Typography component="span">
                (Age: {calculateAge(student.date_of_birth)})
              </Typography>
            </Typography>
            <Typography>
              <Typography component="span" fontWeight="bold">
                Grade:
              </Typography>{" "}
              {GradeLevelLabels[student.grade_level as import("../types/Grade").GradeLevel] || "Not Set"}
            </Typography>
          </Box>
        </Box>

        <Button
          onClick={handleEditStudent}
          variant="solid"
          color="primary"
          sx={{ position: "absolute", top: 16, right: 16 }}
        >
          Edit Student
        </Button>
      </Card>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h3" component="h2">
          Parents / Guardians
        </Typography>
        <ParentGuardiansTable student={student} />
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h3" component="h2">
          Attendance
        </Typography>
        <StudentAttendanceTable student={student} />
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h3" component="h2">
          Classes
        </Typography>
        <ClassesTable
          studentId={student.id}
          isStudentDetailView={true}
        />
      </Box>
    </Page>
  );
};

export default StudentDetailView;
